import { cn } from "@/lib/utils";
import { LIFECYCLE_CONFIG } from "@/lib/lifecycle-config";

type StageState = "completed" | "active" | "upcoming";

// ADV-1347 (lifecycle epic): the stage list is lifted into LIFECYCLE_CONFIG so
// the tracker (current-state axis) and the lifecycle timeline (moment axis)
// share one source of truth and never re-define the job stages twice. The
// tracker only needs the positional key/label here; the config's iconKey/tint
// fields are consumed by the timeline. The positional-state logic below is
// unchanged — only the array source moved.
const PRIMARY_STAGES = LIFECYCLE_CONFIG.job.primaryStages.map((s) => ({
  key: s.key,
  label: s.label,
}));

const ALT_TERMINAL = {
  key: LIFECYCLE_CONFIG.job.terminalStages[0].key,
  label: LIFECYCLE_CONFIG.job.terminalStages[0].label,
};

type AnyStage = "new" | "scheduled" | "in_progress" | "completed" | "canceled";

interface JobStageTrackerProps {
  /**
   * The current job stage. `null`/undefined falls back to `new`.
   * Unrecognized values render as if `new`.
   */
  stage: AnyStage | string | null | undefined;
  /** Optional className for the outer wrapper. */
  className?: string;
}

function normalizeStage(stage: JobStageTrackerProps["stage"]): AnyStage {
  if (!stage) return "new";
  const known: AnyStage[] = [
    "new",
    "scheduled",
    "in_progress",
    "completed",
    "canceled",
  ];
  return (known as readonly string[]).includes(stage)
    ? (stage as AnyStage)
    : "new";
}

function primaryStageState(
  // Widened to `string`: PRIMARY_STAGES is now derived from LIFECYCLE_CONFIG
  // (string-keyed), and the only use is a key comparison in findIndex.
  stageKey: string,
  current: AnyStage,
): StageState {
  if (current === "canceled") return "upcoming";
  const idx = PRIMARY_STAGES.findIndex((s) => s.key === stageKey);
  const cur = PRIMARY_STAGES.findIndex((s) => s.key === current);
  if (idx < cur) return "completed";
  if (idx === cur) return "active";
  return "upcoming";
}

function altTerminalState(current: AnyStage): StageState {
  return current === "canceled" ? "active" : "upcoming";
}

interface NodeProps {
  label: string;
  state: StageState;
}

function StageNode({ label, state }: NodeProps) {
  const diamondColor =
    state === "active"
      ? "var(--color-warning)"
      : state === "completed"
        ? "var(--color-success)"
        : "var(--color-border)";

  return (
    <div data-state={state} className="flex flex-col items-center gap-1.5">
      <div
        className="h-2.5 w-2.5 rotate-45"
        style={{
          backgroundColor: state !== "upcoming" ? diamondColor : "transparent",
          border: `1.5px solid ${diamondColor}`,
        }}
        aria-hidden="true"
      />
      <span
        className={
          state === "active"
            ? "typo-label font-semibold text-[color:var(--color-text-hi)]"
            : state === "completed"
              ? "typo-label text-[color:var(--color-text-md)]"
              : "typo-label text-[color:var(--color-text-lo)]"
        }
      >
        {label}
      </span>
    </div>
  );
}

interface ConnectorProps {
  /** Whether the path BEFORE this connector has been traversed (success colored). */
  passed: boolean;
}

function Connector({ passed }: ConnectorProps) {
  return (
    <div
      className="h-px flex-1 mt-[5px]"
      style={{
        backgroundColor: passed
          ? "var(--color-success)"
          : "var(--color-border)",
      }}
      aria-hidden="true"
    />
  );
}

export function JobStageTracker({ stage, className }: JobStageTrackerProps) {
  const current = normalizeStage(stage);

  return (
    <div className={cn("flex items-start gap-2 w-full", className)}>
      {PRIMARY_STAGES.map((s, i) => {
        const state = primaryStageState(s.key, current);
        const nextState = PRIMARY_STAGES[i + 1]
          ? primaryStageState(PRIMARY_STAGES[i + 1].key, current)
          : "upcoming";
        const passed = state === "completed" && nextState !== "upcoming";
        return (
          <div key={s.key} className="contents">
            <StageNode label={s.label} state={state} />
            {i < PRIMARY_STAGES.length - 1 && <Connector passed={passed} />}
          </div>
        );
      })}

      {/* Vertical divider before alternate terminal state */}
      <div
        data-divider="alt-terminal"
        className="self-stretch w-px mx-2"
        style={{ backgroundColor: "var(--color-border)" }}
        aria-hidden="true"
      />

      <StageNode label={ALT_TERMINAL.label} state={altTerminalState(current)} />
    </div>
  );
}
